import { ChangeDetectionStrategy, Component, computed, effect, input, signal } from '@angular/core';

/**
 * Renders a static waveform visualization from an audio or video URL.
 *
 * Loads the media via the Web Audio API, extracts peak amplitude data per bar,
 * and renders vertically-centred SVG bars. Bars before `progressRatio` are
 * painted with `playedColor`, the rest with `unplayedColor` — so the same
 * component works both as a decorative background (equal colors) and as the
 * track of a waveform seek bar.
 *
 * The optional `startTime` and `duration` inputs control which portion of the
 * audio is visualised. The decoded AudioBuffer is cached per URL so changing the
 * window only repeats the cheap peak-extraction step (no re-download).
 */
@Component({
	selector: 'lib-waveform',
	standalone: true,
	template: `
		@if (bars().length > 0) {
			<svg
				xmlns="http://www.w3.org/2000/svg"
				class="h-full w-full"
				[attr.viewBox]="svgViewBox"
				preserveAspectRatio="none"
				aria-hidden="true"
			>
				@for (bar of bars(); track $index) {
					<rect
						[attr.x]="$index * BAR_SLOT_WIDTH + BAR_GAP"
						[attr.y]="(SVG_HEIGHT - bar) / 2"
						[attr.width]="BAR_WIDTH"
						[attr.height]="bar"
						[attr.fill]="$index < playedBarCount() ? playedColor() : unplayedColor()"
					/>
				}
			</svg>
		}
	`,
	styles: [
		`
			:host {
				display: block;
			}
		`,
	],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WaveformComponent {
	readonly BAR_COUNT = 100;
	readonly BAR_SLOT_WIDTH = 2;
	readonly BAR_GAP = 0.3;
	readonly BAR_WIDTH = 1.4; // BAR_SLOT_WIDTH - 2 * BAR_GAP
	readonly SVG_HEIGHT = 100;
	readonly MIN_BAR_HEIGHT = 2;
	readonly svgViewBox = `0 0 ${this.BAR_COUNT * this.BAR_SLOT_WIDTH} ${this.SVG_HEIGHT}`;

	audioUrl = input<string | null | undefined>(null);
	/** Offset into the source file in seconds from which to start the waveform. Defaults to 0. */
	startTime = input<number | null | undefined>(null);
	/** Duration in seconds of the window to display. Defaults to the full file duration. */
	duration = input<number | null | undefined>(null);

	/** Fraction (0..1) of the waveform considered "played". */
	progressRatio = input<number>(0);
	/** Color of played bars (defaults to current text color). */
	playedColor = input<string>('currentColor');
	/** Color of not-yet-played bars (defaults to current text color). */
	unplayedColor = input<string>('currentColor');

	bars = signal<number[]>([]);

	playedBarCount = computed(() => {
		const ratio = Math.min(1, Math.max(0, this.progressRatio()));
		return Math.round(ratio * this.bars().length);
	});

	/** Tracks the most-recently-requested URL to discard stale async results. */
	private _currentUrl: string | null = null;
	/** Cached decoded audio buffer, keyed by URL to avoid redundant network fetches. */
	private _cachedUrl: string | null = null;
	private _cachedAudioBuffer: AudioBuffer | null = null;

	constructor() {
		effect(() => {
			const url = this.audioUrl();
			const startTime = this.startTime();
			const duration = this.duration();

			if (!url) {
				this._currentUrl = null;
				this._cachedUrl = null;
				this._cachedAudioBuffer = null;
				this.bars.set([]);
				return;
			}

			// Cache hit: AudioBuffer already decoded for this URL — re-extract cheaply.
			if (url === this._cachedUrl && this._cachedAudioBuffer) {
				// Update _currentUrl so any in-flight fetch for a different URL is discarded.
				this._currentUrl = url;
				this.bars.set(this._extractPeaks(this._cachedAudioBuffer, startTime ?? 0, duration ?? null));
				return;
			}

			// Cache miss: fetch and decode the audio file.
			void this._loadWaveform(url);
		});
	}

	private async _loadWaveform(url: string): Promise<void> {
		this._currentUrl = url;
		this.bars.set([]);

		try {
			// Discard stale requests before starting expensive work
			if (this._currentUrl !== url) return;

			const response = await fetch(url);
			if (this._currentUrl !== url) return;

			if (!response.ok) {
				console.warn(`[WaveformComponent] Failed to fetch audio (${response.status}): ${url}`);
				return;
			}

			const arrayBuffer = await response.arrayBuffer();
			if (this._currentUrl !== url) return;

			// OfflineAudioContext is designed for non-realtime processing and doesn't
			// require explicit cleanup like AudioContext does.
			const offlineCtx = new OfflineAudioContext(1, 1, 44100);
			const audioBuffer = await offlineCtx.decodeAudioData(arrayBuffer);
			if (this._currentUrl !== url) return;

			// Populate cache so subsequent startTime/duration changes are handled cheaply.
			// Guard once more: although no await separates us from the check above, being
			// explicit here makes the invariant clear for future readers.
			if (this._currentUrl !== url) return;
			this._cachedUrl = url;
			this._cachedAudioBuffer = audioBuffer;

			// Read the current signal values at completion time to ensure we use the
			// most recent window even if inputs changed while the fetch was in-flight.
			this.bars.set(this._extractPeaks(audioBuffer, this.startTime() ?? 0, this.duration() ?? null));
		} catch {
			// Silently ignore — waveform is purely decorative, errors are non-critical
		}
	}

	/**
	 * Extract BAR_COUNT peak amplitude values from `audioBuffer` within the window
	 * `[offsetSecs, offsetSecs + durationSecs]` (clamped to the file's actual duration).
	 * When `durationSecs` is null the whole file (from `offsetSecs` onward) is used.
	 */
	private _extractPeaks(audioBuffer: AudioBuffer, offsetSecs: number, durationSecs: number | null): number[] {
		const sampleRate = audioBuffer.sampleRate;
		const totalSamples = audioBuffer.length;

		const startSample = Math.max(0, Math.round(offsetSecs * sampleRate));
		const endSample = durationSecs !== null ? Math.min(totalSamples, startSample + Math.round(durationSecs * sampleRate)) : totalSamples;

		const rangeLength = endSample - startSample;
		if (rangeLength <= 0) {
			return new Array<number>(this.BAR_COUNT).fill(this.MIN_BAR_HEIGHT);
		}

		const channelData = audioBuffer.getChannelData(0);
		const samplesPerBar = Math.max(1, Math.floor(rangeLength / this.BAR_COUNT));
		const bars: number[] = [];

		for (let i = 0; i < this.BAR_COUNT; i++) {
			const start = startSample + i * samplesPerBar;
			const end = Math.min(start + samplesPerBar, endSample);

			// Pad with minimum bar height if we have stepped past the end of the range
			if (start >= endSample) {
				bars.push(this.MIN_BAR_HEIGHT);
				continue;
			}

			let peak = 0;
			for (let j = start; j < end; j++) {
				const abs = Math.abs(channelData[j]);
				if (abs > peak) peak = abs;
			}

			// Scale peak (0–1) to SVG_HEIGHT; enforce a minimum height for visibility
			bars.push(Math.max(this.MIN_BAR_HEIGHT, peak * this.SVG_HEIGHT));
		}

		return bars;
	}
}
